import { sampleData } from '../../../../../DummyData/ConvertSampleJson';
import React, { useState } from 'react';

// Define types for our component
interface JsonToPythonProps {}

// Python generation options
type PythonOptions = {
  className: string;
  usePydantic: boolean;
  useDataclasses: boolean;
  generateDocstrings: boolean;
  generateTypeHints: boolean;
  generateJsonMethods: boolean;
  generateRepr: boolean;
  generateValidation: boolean;
  indentationSpaces: number;
  pythonVersion: '3.6+' | '3.7+' | '3.8+' | '3.9+' | '3.10+';
}

// Support for different naming conventions
const toPascalCase = (str: string): string => {
  if (!str) return '';
  return str
    .replace(/[-_](\w)/g, (_, c) => c.toUpperCase())
    .replace(/^\w/, c => c.toUpperCase());
};

const toSnakeCase = (str: string): string => {
  if (!str) return '';
  return str
    .replace(/([A-Z])/g, '_$1')
    .replace(/^_/, '')
    .replace(/[-\s]+/g, '_')
    .toLowerCase();
};

const JsonToPython: React.FC<JsonToPythonProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the Python output
  const [pythonOutput, setPythonOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the root class name
  const [rootClassName, setRootClassName] = useState<string>('RootClass');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isCodeGenerated, setIsCodeGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for Python options
  const [pythonOptions, setPythonOptions] = useState<PythonOptions>({
    className: 'RootClass',
    usePydantic: true,
    useDataclasses: false,
    generateDocstrings: true,
    generateTypeHints: true,
    generateJsonMethods: true,
    generateRepr: false,
    generateValidation: false,
    indentationSpaces: 4,
    pythonVersion: '3.9+'
  });

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsCodeGenerated(false);
  };

  // Handle root class name change
  const handleRootClassNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRootClassName(e.target.value);
    setPythonOptions({...pythonOptions, className: e.target.value});
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsCodeGenerated(false);
    setPythonOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setPythonOutput('');
    setError('');
    setRootClassName('RootClass');
    setPythonOptions({
      className: 'RootClass',
      usePydantic: true,
      useDataclasses: false,
      generateDocstrings: true,
      generateTypeHints: true,
      generateJsonMethods: true,
      generateRepr: false,
      generateValidation: false,
      indentationSpaces: 4,
      pythonVersion: '3.9+'
    });
    setIsCodeGenerated(false);
  };

  // Handle change in Python options
  const handleOptionChange = (option: keyof PythonOptions, value: any) => {
    setPythonOptions({...pythonOptions, [option]: value});
    
    // Handle conflicting options
    if (option === 'usePydantic' && value === true) {
      setPythonOptions(prev => ({...prev, useDataclasses: false, [option]: value}));
    } else if (option === 'useDataclasses' && value === true) {
      setPythonOptions(prev => ({...prev, usePydantic: false, [option]: value}));
    }
  };

  // Get Python type for a JavaScript value
  const getPythonType = (value: any, isArray = false): string => {
    if (value === null || value === undefined) return "None";
    
    const type = typeof value;
    
    switch (type) {
      case 'string':
        return isArray ? "List[str]" : "str";
      case 'number':
        // Check if the number is an integer
        return isArray 
          ? Number.isInteger(value) ? "List[int]" : "List[float]" 
          : Number.isInteger(value) ? "int" : "float";
      case 'boolean':
        return isArray ? "List[bool]" : "bool";
      case 'object':
        if (Array.isArray(value)) {
          if (value.length === 0) return "List";
          return `List[${getPythonType(value[0])}]`;
        }
        const className = getClassName(value);
        return isArray ? `List[${className}]` : className;
      default:
        return isArray ? "List" : "Any";
    }
  };

  // Get a class name for an object
  const getClassName = (obj: any): string => {
    if (!obj) return "Dict";
    
    // Try to infer a meaningful name from object properties
    if (obj.name && typeof obj.name === 'string') {
      return toPascalCase(obj.name);
    }
    if (obj.type && typeof obj.type === 'string') {
      return toPascalCase(obj.type);
    }
    if (obj.title && typeof obj.title === 'string') {
      return toPascalCase(obj.title);
    }
    
    // Default to a generic name
    return "CustomObject";
  };

  // Generate indentation
  const getIndent = (level: number): string => {
    return ' '.repeat(pythonOptions.indentationSpaces * level);
  };

  // Generate Python docstring
  const generateDocstring = (className: string, fields: {name: string, type: string, description: string}[]): string => {
    const indent = getIndent(1);
    let result = `${indent}"""\n`;
    result += `${indent}${className} class\n`;
    result += `${indent}\n`;
    
    if (fields.length > 0) {
      result += `${indent}Attributes:\n`;
      fields.forEach(field => {
        result += `${indent}    ${toSnakeCase(field.name)} (${field.type}): ${field.description || 'Description for ' + field.name}\n`;
      });
    }
    
    result += `${indent}"""\n`;
    
    return result;
  };

  // Generate Pydantic model
  const generatePydanticModel = (
    obj: any,
    className: string,
    indentLevel: number,
    processedClasses: Set<string>,
    classDefinitions: string[] = []
  ): string => {
    const indent = getIndent(indentLevel);
    let result = `${indent}class ${className}(BaseModel):\n`;
    
    // Generate docstring if requested
    if (pythonOptions.generateDocstrings) {
      const fields = Object.keys(obj).map(key => {
        return {
          name: key,
          type: getPythonType(obj[key]),
          description: ''
        };
      });
      
      result += generateDocstring(className, fields);
    }
    
    // Add field declarations
    const keys = Object.keys(obj);
    
    // If no fields, add a pass statement
    if (keys.length === 0) {
      result += `${indent}${getIndent(1)}pass\n`;
      
      classDefinitions.push(result);
      return classDefinitions.join('\n\n');
    }
    
    for (const key of keys) {
      const value = obj[key];
      let fieldType = getPythonType(value);
      
      // For objects, create a nested class
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const nestedClassName = toPascalCase(key);
        
        // Generate nested class if not processed already
        if (!processedClasses.has(nestedClassName)) {
          processedClasses.add(nestedClassName);
          generatePydanticModel(value, nestedClassName, indentLevel, processedClasses, classDefinitions);
        }
      } 
      // For arrays with object elements, create a nested class for the elements
      else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        const itemClassName = toPascalCase(key.replace(/s$/, ''));  // Singular form for item type
        
        // Generate nested class if not processed already
        if (!processedClasses.has(itemClassName)) {
          processedClasses.add(itemClassName);
          generatePydanticModel(value[0], itemClassName, indentLevel, processedClasses, classDefinitions);
        }
      }
      
      const snakeCaseKey = toSnakeCase(key);
      
      // Field declaration with type annotation
      if (pythonOptions.generateTypeHints) {
        // For optional fields (nullable values)
        if (value === null) {
          result += `${indent}${getIndent(1)}${snakeCaseKey}: Optional[${fieldType}] = None\n`;
        } else {
          result += `${indent}${getIndent(1)}${snakeCaseKey}: ${fieldType}\n`;
        }
      } else {
        // Without type annotations
        result += `${indent}${getIndent(1)}${snakeCaseKey} = None\n`;
      }
    }
    
    // Add validation if requested
    if (pythonOptions.generateValidation) {
      result += '\n';
      result += `${indent}${getIndent(1)}@validator('*')\n`;
      result += `${indent}${getIndent(1)}def check_values(cls, v, values, **kwargs):\n`;
      result += `${indent}${getIndent(2)}return v\n`;
    }
    
    // Add JSON methods if requested
    if (pythonOptions.generateJsonMethods) {
      result += '\n';
      result += `${indent}${getIndent(1)}def to_json(self) -> str:\n`;
      result += `${indent}${getIndent(2)}return self.json()\n\n`;
      
      result += `${indent}${getIndent(1)}@classmethod\n`;
      result += `${indent}${getIndent(1)}def from_json(cls, json_str: str) -> "${className}":\n`;
      result += `${indent}${getIndent(2)}return cls.parse_raw(json_str)\n`;
    }
    
    classDefinitions.push(result);
    
    return classDefinitions.join('\n\n');
  };

  // Generate Dataclass
  const generateDataclass = (
    obj: any,
    className: string,
    indentLevel: number,
    processedClasses: Set<string>,
    classDefinitions: string[] = []
  ): string => {
    const indent = getIndent(indentLevel);
    let result = `${indent}@dataclass\n`;
    result += `${indent}class ${className}:\n`;
    
    // Generate docstring if requested
    if (pythonOptions.generateDocstrings) {
      const fields = Object.keys(obj).map(key => {
        return {
          name: key,
          type: getPythonType(obj[key]),
          description: ''
        };
      });
      
      result += generateDocstring(className, fields);
    }
    
    // Add field declarations
    const keys = Object.keys(obj);
    
    // If no fields, add a pass statement
    if (keys.length === 0) {
      result += `${indent}${getIndent(1)}pass\n`;
      
      classDefinitions.push(result);
      return classDefinitions.join('\n\n');
    }
    
    for (const key of keys) {
      const value = obj[key];
      let fieldType = getPythonType(value);
      
      // For objects, create a nested class
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const nestedClassName = toPascalCase(key);
        
        // Generate nested class if not processed already
        if (!processedClasses.has(nestedClassName)) {
          processedClasses.add(nestedClassName);
          generateDataclass(value, nestedClassName, indentLevel, processedClasses, classDefinitions);
        }
      } 
      // For arrays with object elements, create a nested class for the elements
      else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        const itemClassName = toPascalCase(key.replace(/s$/, ''));  // Singular form for item type
        
        // Generate nested class if not processed already
        if (!processedClasses.has(itemClassName)) {
          processedClasses.add(itemClassName);
          generateDataclass(value[0], itemClassName, indentLevel, processedClasses, classDefinitions);
        }
      }
      
      const snakeCaseKey = toSnakeCase(key);
      
      // Field declaration with type annotation
      if (pythonOptions.generateTypeHints) {
        // For optional fields (nullable values)
        if (value === null) {
          result += `${indent}${getIndent(1)}${snakeCaseKey}: Optional[${fieldType}] = None\n`;
        } else {
          result += `${indent}${getIndent(1)}${snakeCaseKey}: ${fieldType}\n`;
        }
      } else {
        // Without type annotations
        result += `${indent}${getIndent(1)}${snakeCaseKey}: Any\n`;
      }
    }
    
    // Add __repr__ method if requested
    if (pythonOptions.generateRepr) {
      result += '\n';
      result += `${indent}${getIndent(1)}def __repr__(self) -> str:\n`;
      result += `${indent}${getIndent(2)}return f"${className}(${keys.map(k => `${toSnakeCase(k)}={self.${toSnakeCase(k)}}`).join(', ')})"\n`;
    }
    
    // Add JSON methods if requested
    if (pythonOptions.generateJsonMethods) {
      result += '\n';
      result += `${indent}${getIndent(1)}def to_json(self) -> str:\n`;
      result += `${indent}${getIndent(2)}return json.dumps(asdict(self), indent=2)\n\n`;
      
      result += `${indent}${getIndent(1)}@classmethod\n`;
      result += `${indent}${getIndent(1)}def from_json(cls, json_str: str) -> "${className}":\n`;
      result += `${indent}${getIndent(2)}data = json.loads(json_str)\n`;
      result += `${indent}${getIndent(2)}return cls(**data)\n`;
    }
    
    classDefinitions.push(result);
    
    return classDefinitions.join('\n\n');
  };

  // Generate simple Python class
  const generateSimpleClass = (
    obj: any,
    className: string,
    indentLevel: number,
    processedClasses: Set<string>,
    classDefinitions: string[] = []
  ): string => {
    const indent = getIndent(indentLevel);
    let result = `${indent}class ${className}:\n`;
    
    // Generate docstring if requested
    if (pythonOptions.generateDocstrings) {
      const fields = Object.keys(obj).map(key => {
        return {
          name: key,
          type: getPythonType(obj[key]),
          description: ''
        };
      });
      
      result += generateDocstring(className, fields);
    }
    
    // Add __init__ method
    result += `${indent}${getIndent(1)}def __init__(self`;
    
    // Add parameters to __init__
    const keys = Object.keys(obj);
    
    // If no fields, empty __init__
    if (keys.length === 0) {
      result += `):\n`;
      result += `${indent}${getIndent(2)}pass\n`;
      
      classDefinitions.push(result);
      return classDefinitions.join('\n\n');
    }
    
    // Process fields
    const fieldDefinitions: string[] = [];
    
    for (const key of keys) {
      const value = obj[key];
      let fieldType = getPythonType(value);
      
      // For objects, create a nested class
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const nestedClassName = toPascalCase(key);
        
        // Generate nested class if not processed already
        if (!processedClasses.has(nestedClassName)) {
          processedClasses.add(nestedClassName);
          generateSimpleClass(value, nestedClassName, indentLevel, processedClasses, classDefinitions);
        }
      } 
      // For arrays with object elements, create a nested class for the elements
      else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        const itemClassName = toPascalCase(key.replace(/s$/, ''));  // Singular form for item type
        
        // Generate nested class if not processed already
        if (!processedClasses.has(itemClassName)) {
          processedClasses.add(itemClassName);
          generateSimpleClass(value[0], itemClassName, indentLevel, processedClasses, classDefinitions);
        }
      }
      
      const snakeCaseKey = toSnakeCase(key);
      
      // Add parameter with type hint
      if (pythonOptions.generateTypeHints) {
        if (value === null) {
          result += `, ${snakeCaseKey}: Optional[${fieldType}] = None`;
        } else {
          result += `, ${snakeCaseKey}: ${fieldType}`;
        }
      } else {
        if (value === null) {
          result += `, ${snakeCaseKey}=None`;
        } else {
          result += `, ${snakeCaseKey}`;
        }
      }
      
      // Add field assignment
      fieldDefinitions.push(`${indent}${getIndent(2)}self.${snakeCaseKey} = ${snakeCaseKey}`);
    }
    
    // Close __init__ parameter list and add function body
    result += `):\n`;
    result += fieldDefinitions.join('\n') + '\n';
    
    // Add __repr__ method if requested
    if (pythonOptions.generateRepr) {
      result += '\n';
      result += `${indent}${getIndent(1)}def __repr__(self) -> str:\n`;
      result += `${indent}${getIndent(2)}return f"${className}(${keys.map(k => `${toSnakeCase(k)}={self.${toSnakeCase(k)}}`).join(', ')})"\n`;
    }
    
    // Add JSON methods if requested
    if (pythonOptions.generateJsonMethods) {
      result += '\n';
      result += `${indent}${getIndent(1)}def to_json(self) -> str:\n`;
      result += `${indent}${getIndent(2)}return json.dumps(self.__dict__, indent=2)\n\n`;
      
      result += `${indent}${getIndent(1)}@classmethod\n`;
      result += `${indent}${getIndent(1)}def from_json(cls, json_str: str) -> "${className}":\n`;
      result += `${indent}${getIndent(2)}data = json.loads(json_str)\n`;
      result += `${indent}${getIndent(2)}return cls(**data)\n`;
    }
    
    classDefinitions.push(result);
    
    return classDefinitions.join('\n\n');
  };

  // Generate full Python code with imports
  const generateFullPythonCode = (jsonObj: any): string => {
    const processedClasses = new Set<string>();
    const classDefinitions: string[] = [];
    
    let imports = "import json\n";
    if (pythonOptions.generateTypeHints) {
      imports += `from typing import List, Dict, Optional, Any${pythonOptions.pythonVersion >= '3.10+' ? ', TypedDict' : ''}\n`;
    }
    
    if (pythonOptions.usePydantic) {
      imports += "from pydantic import BaseModel";
      if (pythonOptions.generateValidation) {
        imports += ", validator";
      }
      imports += "\n";
    } else if (pythonOptions.useDataclasses) {
      imports += "from dataclasses import dataclass, asdict\n";
    }
    
    imports += "\n\n";
    
    // Generate code based on the option
    let classesCode;
    if (pythonOptions.usePydantic) {
      classesCode = generatePydanticModel(jsonObj, rootClassName, 0, processedClasses, classDefinitions);
    } else if (pythonOptions.useDataclasses) {
      classesCode = generateDataclass(jsonObj, rootClassName, 0, processedClasses, classDefinitions);
    } else {
      classesCode = generateSimpleClass(jsonObj, rootClassName, 0, processedClasses, classDefinitions);
    }
    
    // Example usage
    let example = '\n\n# Example usage:\n';
    example += 'if __name__ == "__main__":\n';
    example += `    # Create from JSON string\n`;
    example += `    json_str = \'\'\'${JSON.stringify(jsonObj, null, 4)}\'\'\'\n`;
    example += `    obj = ${rootClassName}.from_json(json_str)\n`;
    example += `    print(obj)\n\n`;
    example += `    # Convert back to JSON\n`;
    example += `    json_output = obj.to_json()\n`;
    example += `    print(json_output)\n`;
    
    return imports + classesCode + example;
  };

  // Convert JSON to Python classes
  const convertJsonToPython = () => {
    setError('');
    setIsLoading(true);
    setIsCodeGenerated(false);
    setPythonOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      const pythonCode = generateFullPythonCode(jsonData);
      
      setIsCodeGenerated(true);
      setPythonOutput(pythonCode);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating Python code: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy Python code to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(pythonOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download Python file
  const downloadPython = () => {
    const blob = new Blob([pythonOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${rootClassName.toLowerCase()}.py`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to Python Class Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to Python classes. Paste your JSON data below to generate strongly-typed Python class definitions with optional Pydantic or dataclass support.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="rootClassName" className="block text-sm font-medium text-gray-700 mb-1">
                Root Class Name
              </label>
              <input
                type="text"
                id="rootClassName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={rootClassName}
                onChange={handleRootClassNameChange}
                placeholder="Enter root class name"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="usePydantic"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={pythonOptions.usePydantic}
                    onChange={(e) => handleOptionChange('usePydantic', e.target.checked)}
                  />
                  <label htmlFor="usePydantic" className="ml-2 block text-sm text-gray-700">
                    Use Pydantic Models
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="useDataclasses"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={pythonOptions.useDataclasses}
                    onChange={(e) => handleOptionChange('useDataclasses', e.target.checked)}
                  />
                  <label htmlFor="useDataclasses" className="ml-2 block text-sm text-gray-700">
                    Use Dataclasses
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="generateDocstrings"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={pythonOptions.generateDocstrings}
                    onChange={(e) => handleOptionChange('generateDocstrings', e.target.checked)}
                  />
                  <label htmlFor="generateDocstrings" className="ml-2 block text-sm text-gray-700">
                    Generate Docstrings
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="generateTypeHints"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={pythonOptions.generateTypeHints}
                    onChange={(e) => handleOptionChange('generateTypeHints', e.target.checked)}
                  />
                  <label htmlFor="generateTypeHints" className="ml-2 block text-sm text-gray-700">
                    Generate Type Hints
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="generateJsonMethods"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={pythonOptions.generateJsonMethods}
                    onChange={(e) => handleOptionChange('generateJsonMethods', e.target.checked)}
                  />
                  <label htmlFor="generateJsonMethods" className="ml-2 block text-sm text-gray-700">
                    Generate JSON Methods
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="generateRepr"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={pythonOptions.generateRepr}
                    onChange={(e) => handleOptionChange('generateRepr', e.target.checked)}
                  />
                  <label htmlFor="generateRepr" className="ml-2 block text-sm text-gray-700">
                    Generate __repr__ Method
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="generateValidation"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={pythonOptions.generateValidation}
                    onChange={(e) => handleOptionChange('generateValidation', e.target.checked)}
                    disabled={!pythonOptions.usePydantic}
                  />
                  <label htmlFor="generateValidation" className={`ml-2 block text-sm ${!pythonOptions.usePydantic ? 'text-gray-400' : 'text-gray-700'}`}>
                    Generate Validation Methods (Pydantic only)
                  </label>
                </div>
                
                <div>
                  <label htmlFor="pythonVersion" className="block text-sm font-medium text-gray-700 mb-1">
                    Python Version
                  </label>
                  <select
                    id="pythonVersion"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={pythonOptions.pythonVersion}
                    onChange={(e) => handleOptionChange('pythonVersion', e.target.value)}
                  >
                    <option value="3.6+">Python 3.6+</option>
                    <option value="3.7+">Python 3.7+</option>
                    <option value="3.8+">Python 3.8+</option>
                    <option value="3.9+">Python 3.9+</option>
                    <option value="3.10+">Python 3.10+</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="indentationSpaces" className="block text-sm font-medium text-gray-700 mb-1">
                    Indentation Spaces
                  </label>
                  <input
                    type="number"
                    id="indentationSpaces"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={pythonOptions.indentationSpaces}
                    onChange={(e) => handleOptionChange('indentationSpaces', parseInt(e.target.value) || 4)}
                    min={1}
                    max={8}
                  />
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToPython}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to Python'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Python Output
            </label>
            
            {isCodeGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadPython}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isCodeGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  <p>
                    Python code will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isCodeGenerated && (
              <div className="flex-grow flex flex-col">
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="pythonOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={pythonOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isCodeGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">Python Classes Successfully Generated!</p>
              </div>
              <p className="text-sm">
                {pythonOptions.usePydantic
                  ? "The generated code uses Pydantic models for data validation and serialization. Make sure to install Pydantic in your project with 'pip install pydantic'."
                  : pythonOptions.useDataclasses
                    ? "The generated code uses Python dataclasses for clean data representation. Available in Python 3.7+."
                    : "The generated code includes standard Python classes with all necessary methods."}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to Python Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool analyzes your JSON data structure and generates corresponding Python classes. It automatically 
              determines appropriate types, creates nested classes for complex objects, and maintains the 
              relationships between your data models.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Type Inference</strong> - Automatically determines Python types from JSON values</li>
              <li><strong>Type Hints</strong> - Adds Python type annotations for better IDE support and static type checking</li>
              <li><strong>Nested Classes</strong> - Creates separate classes for complex nested objects</li>
              <li><strong>Collection Support</strong> - Properly handles arrays as List[T] types</li>
              <li><strong>Pydantic Models</strong> - Option to use Pydantic for data validation and serialization</li>
              <li><strong>Dataclasses</strong> - Option to use Python's built-in dataclasses for cleaner code</li>
              <li><strong>PEP 8 Compliance</strong> - Generates code following Python style guidelines</li>
              <li><strong>JSON Serialization</strong> - Includes methods for converting to and from JSON</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Class Generation Options</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Standard Classes</strong> - Simple Python classes with constructors and properties</li>
              <li><strong>Dataclasses</strong> - Uses Python's dataclass decorator (Python 3.7+) for cleaner class definitions</li>
              <li><strong>Pydantic Models</strong> - Uses Pydantic for automatic validation and serialization</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>Give a meaningful name to your root class that reflects the data it represents</li>
              <li>For arrays, the converter will try to infer the type from the first element, so ensure your sample JSON includes representative data</li>
              <li>Enable Pydantic for data validation and easy JSON serialization</li>
              <li>Use type hints for better IDE support and static type checking</li>
              <li>Choose the appropriate Python version for your project requirements</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Python Code Usage</h3>
            <p className="text-gray-600">
              Once you've generated your Python classes, you can use them with JSON data:
            </p>
            <div className="mt-2 bg-gray-100 p-3 rounded-md">
              <code className="text-sm font-mono">
                # Parse JSON string into your class<br/>
                {/* json_str = '{"name": "John", "age": 30}'<br/> */}
                user = User.from_json(json_str)<br/>
                <br/>
                # Access properties<br/>
                print(user.name)  # Output: John<br/>
                <br/>
                # Convert back to JSON<br/>
                json_output = user.to_json()<br/>
                print(json_output)
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToPython;